"use client";

import { ThemeToggle } from '@/components/ThemeToggle';
import { AudienceToggle, getStoredAudience, type AudienceMode } from '@/components/AudienceToggle';
import Link from 'next/link';
import { useEffect, useRef, useState, useMemo } from 'react';

function HomeContent() {
	const [viewMode, setViewMode] = useState<AudienceMode>('private');
	const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
	const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

	// Initialize viewMode from URL or sessionStorage
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const params = new URLSearchParams(window.location.search);
		const urlAudience = params.get('audience') || params.get('type');
		if (urlAudience === 'company') setViewMode('company');
		else if (urlAudience === 'private') setViewMode('private');
		else {
			const stored = getStoredAudience();
			if (stored) setViewMode(stored);
		}
	}, []);

	// Update URL when viewMode changes (without full navigation) - only when company for clean URLs
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const url = new URL(window.location.href);
		if (viewMode === 'company') {
			url.searchParams.set('audience', 'company');
		} else {
			url.searchParams.delete('audience');
			url.searchParams.delete('type');
		}
		window.history.replaceState({}, '', url.toString());
	}, [viewMode]);
	const heroRef = useRef<HTMLElement>(null);
	const expertiseRef = useRef<HTMLDivElement>(null);
	const featuresRef = useRef<HTMLDivElement>(null);
	const statsRef = useRef<HTMLDivElement>(null);
	const howItWorksRef = useRef<HTMLDivElement>(null);
	const testimonialsRef = useRef<HTMLDivElement>(null);
	const faqRef = useRef<HTMLDivElement>(null);

	// Intersection Observer for scroll animations (optimized with passive listeners)
	useEffect(() => {
		// Check if user prefers reduced motion
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		
		// If reduced motion is preferred, show all sections immediately
		if (prefersReducedMotion) {
			setVisibleSections(new Set(['hero', 'expertise', 'features', 'stats', 'how-it-works', 'testimonials', 'faq']));
			return;
		}
		
		const observerOptions: IntersectionObserverInit = {
			threshold: 0.1,
			rootMargin: '0px 0px -100px 0px',
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setVisibleSections((prev) => new Set(prev).add(entry.target.id));
					// Unobserve after first intersection for better performance
					observer.unobserve(entry.target);
				}
			});
		}, observerOptions);

		const sections = [
			heroRef.current,
			expertiseRef.current,
			featuresRef.current,
			statsRef.current,
			howItWorksRef.current,
			testimonialsRef.current,
			faqRef.current,
		].filter(Boolean) as Element[];

		sections.forEach((section) => {
			if (section) observer.observe(section);
		});

		// Mark hero as visible immediately
		if (heroRef.current) {
			setVisibleSections((prev) => new Set(prev).add('hero'));
		}

		return () => {
			sections.forEach((section) => {
				if (section) observer.unobserve(section);
			});
		};
	}, []);

	const faqs = useMemo(() => [
		{
			question: 'Hur fungerar Karrelo?',
			answer: (
				<>
					Karrelo kombinerar 20+ års rekryteringskunskap med AI för att analysera din profil, ge dig träffsäkra jobbförslag och hjälpa dig förbättra din ansökan.
					<br /><br />
					Du får även stöd med CV, personligt brev och vägledning genom hela processen.
				</>
			),
		},
		{
			question: 'Är det gratis att använda?',
			answer: (
				<>
					Ja — Karrelo är gratis att använda just nu.
					<br /><br />
					Vi utvecklar tjänsten och förbättrar den tillsammans med våra användare.
					<br /><br />
					När vi lanserar betalpaket längre fram kommer vi alltid att informera i god tid — och du kommer aldrig bli debiterad utan att du själv väljer det.
				</>
			),
		},
		{
			question: 'Vad gör Karrelo bättre än andra tjänster?',
			answer: (
				<>
					Karrelo är utvecklat av riktiga karriärcoacher och rekryterare, inte bara AI.
					<br /><br />
					Det betyder att råden och matchningarna bygger på vad som faktiskt fungerar i verkliga rekryteringar.
				</>
			),
		},
		{
			question: 'Hur säkert är min data?',
			answer: (
				<>
					Din data är krypterad och lagras i Sverige/EU enligt GDPR.
					<br /><br />
					Vi säljer aldrig information till tredje part och du kan radera allt när du vill.
					<br /><br />
					Endast du har tillgång till ditt innehåll.
				</>
			),
		},
		{
			question: 'Fungerar det för alla branscher?',
			answer: (
				<>
					Ja – Karrelo anpassar analys, matchningar och rekommendationer efter just din bakgrund, oavsett bransch.
					<br /><br />
					Vi skapar alltid träffsäkra förslag och 100% ATS-anpassade ansökningshandlingar som ger dig bättre chans att sticka ut.
					<br /><br />
					Men det är viktigt att vara ärlig: vissa branscher är just nu mer konkurrensutsatta än andra.
					<br /><br />
					Karrelo kan därför inte garantera ett jobb, men vi lovar att du får de bästa möjliga förutsättningarna genom tydlig matchning, optimerade handlingar och vägledning som faktiskt fungerar.
				</>
			),
		},
	], []);

	const companyFaqs = useMemo(() => [
		{
			question: 'Hur fungerar Karrelo Pro ATS?',
			answer: (
				<>
					Karrelo Pro är ett Applicant Tracking System (ATS) som hjälper företag att rekrytera. Ni skapar era jobbannonser med AI-hjälp, tar emot ansökningar, får AI-poängsättning av kandidater och hanterar hela processen i en anpassningsbar pipeline.
					<br /><br />
					Ni kan bjuda in teammedlemmar, använda e-postmallar och följa analys. Det är byggt på samma rekryteringskunskap som Karrelo för jobbsökare.
				</>
			),
		},
		{
			question: 'Vilka team-roller finns?',
			answer: (
				<>
					Karrelo Pro stödjer fyra roller: Admin (full kontroll), Rekryterare (hantera jobb och ansökningar), Hiring Manager (granska kandidater) och Viewer (endast läsa). Som admin kan ni bjuda in teammedlemmar och tilldela roller.
				</>
			),
		},
		{
			question: 'Hur skapar man jobbannonser med AI?',
			answer: (
				<>
					Ange jobbtitel, plats och er webbplats. Vår AI extraherar information från webbplatsen och genererar en färdig beskrivning. Ni kan redigera och anpassa innan ni publicerar. Kandidater som använder Karrelo får syn på era jobb direkt.
				</>
			),
		},
		{
			question: 'Kan man anpassa rekryteringspipelinen?',
			answer: (
				<>
					Ja. Ni kan skapa egna faser (t.ex. Ansökan, Screening, Intervju, Erbjudande) och dra kandidater mellan dem. Det finns även färdiga mallar för vanliga rekryteringsflöden.
				</>
			),
		},
		{
			question: 'Hur kontaktar jag er för demo eller frågor?',
			answer: (
				<>
					Skicka ett meddelande via vår kontaktsida. Vi återkommer så snart vi kan och kan boka en demo för att visa Karrelo Pro.
				</>
			),
		},
	], []);

	return (
		<>
			{/* Skip to main content link for accessibility */}
			<a
				href="#hero"
				className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
			>
				Hoppa till huvudinnehåll
			</a>
			
			{/* SEO Structured Data */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'SoftwareApplication',
						name: 'Karrelo',
						applicationCategory: 'JobSearchApplication',
						operatingSystem: 'Web',
						offers: {
							'@type': 'Offer',
							price: '0',
							priceCurrency: 'SEK',
							availability: 'https://schema.org/InStock'
						},
						description: 'AI-driven jobbsökning med ESCO-baserad matchning och ATS-optimerade ansökningsbrev',
						featureList: [
							'AI-powered job matching',
							'ESCO-based profile matching',
							'ATS-friendly application letters',
							'Transparent match scoring',
							'Application tracking',
							'Career path exploration'
						],
						aggregateRating: {
							'@type': 'AggregateRating',
							ratingValue: '4.8',
							reviewCount: '150'
						},
						author: {
							'@type': 'Organization',
							name: 'Karrelo',
							description: 'Utvecklat av karriärcoacher och rekryterare med 20+ års erfarenhet'
						}
					})
				}}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'Organization',
						name: 'Karrelo',
						description: 'AI-driven jobbsökning med färre men bättre ansökningar',
						url: typeof window !== 'undefined' ? window.location.origin : 'https://karrelo.com',
						logo: typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : 'https://karrelo.com/logo.png',
						sameAs: [
							// Add social media links when available
						],
						contactPoint: {
							'@type': 'ContactPoint',
							contactType: 'Customer Service',
							email: 'support@karrelo.com' // Using verified domain
						}
					})
				}}
			/>
			<main className="min-h-screen flex flex-col relative overflow-x-hidden">
				{/* Animated Background Elements */}
			<div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
				<div className="absolute top-20 left-10 w-72 h-72 bg-primary-200/20 dark:bg-primary-800/20 rounded-full blur-3xl animate-float"></div>
				<div className="absolute top-40 right-20 w-96 h-96 bg-teal-200/20 dark:bg-teal-800/20 rounded-full blur-3xl animate-float-slow" style={{ animationDelay: '1s' }}></div>
				<div className="absolute bottom-20 left-1/4 w-80 h-80 bg-primary-300/20 dark:bg-primary-700/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
			</div>

			{/* Header with Audience Toggle and Theme Toggle */}
			<header className="relative z-10 w-full p-6 flex justify-end items-center gap-4" role="banner">
				<nav aria-label="Huvudnavigation" className="flex items-center gap-4">
					<div className={`transition-all duration-700 ${visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
						<AudienceToggle value={viewMode} onChange={setViewMode} />
					</div>
					<div className={`transition-all duration-700 ${visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
						<ThemeToggle />
					</div>
				</nav>
			</header>

			{/* Hero Section */}
			<section
				ref={heroRef}
				id="hero"
				className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 pt-3 pb-12 sm:pt-5 sm:pb-20 md:pt-8 md:pb-32 min-h-screen"
				aria-label="Huvudsektion"
			>
				<div className="text-center space-y-6 sm:space-y-8 w-full max-w-5xl mx-auto">
					{viewMode === 'private' ? (
						<>
							{/* Logo/Title with staggered animation */}
							<div className="space-y-4 sm:space-y-6">
								<h1
									className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold transition-all duration-1000 ${
										visibleSections.has('hero')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{ transitionDelay: '0.2s' }}
								>
									<span className="text-gradient bg-gradient-to-r from-primary-600 via-teal-500 to-primary-600 bg-clip-text text-transparent animate-gradient">
										Karrelo
									</span>
								</h1>
								<div className="space-y-2 sm:space-y-3">
									<p
										className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-700 dark:text-slate-300 max-w-4xl mx-auto transition-all duration-1000 ${
											visibleSections.has('hero')
												? 'opacity-100 translate-y-0'
												: 'opacity-0 translate-y-10'
										}`}
										style={{ transitionDelay: '0.4s' }}
									>
										Ett smartare sätt att söka jobb – och mycket mer.
									</p>
									<p
										className={`text-base sm:text-lg md:text-xl lg:text-2xl font-medium text-slate-600 dark:text-slate-400 max-w-3xl mx-auto transition-all duration-1000 ${
											visibleSections.has('hero')
												? 'opacity-100 translate-y-0'
												: 'opacity-0 translate-y-10'
										}`}
										style={{ transitionDelay: '0.5s' }}
									>
										Byggt på 20+ års erfarenhet av svensk arbetsmarknad
									</p>
								</div>
								<p
									className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-primary-600 dark:text-primary-400 max-w-3xl mx-auto transition-all duration-1000 ${
										visibleSections.has('hero')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{ transitionDelay: '0.6s' }}
								>
									Förstärkt av AI – inte ersatt av den.
								</p>
								<div
									className={`text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-4xl mx-auto transition-all duration-1000 leading-relaxed pt-2 sm:pt-4 space-y-2 ${
										visibleSections.has('hero')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{ transitionDelay: '0.8s' }}
								>
									<p>Karrelo bygger på metoder som använts i verklig jobbcoachning i över två decennier.</p>
									<p>Nu förstärkta av AI för att ge dig färre ansökningar, bättre träffsäkerhet och ansökningar som faktiskt blir lästa.</p>
								</div>
							</div>

							{/* CTA Buttons */}
							<div
								className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center pt-4 sm:pt-6 transition-all duration-1000 w-full sm:w-auto ${
									visibleSections.has('hero')
										? 'opacity-100 translate-y-0'
										: 'opacity-0 translate-y-10'
								}`}
								style={{ transitionDelay: '1.2s' }}
							>
								<Link
									href="/signup"
									className="btn-primary w-full sm:w-auto sm:min-w-[220px] text-center ripple hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Skapa konto på Karrelo"
								>
									<span className="relative z-10">Skapa konto</span>
									<div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
								</Link>
								<Link
									href="/login"
									className="btn-outline w-full sm:w-auto sm:min-w-[220px] text-center hover:scale-105 transition-all duration-300 hover:bg-primary-50 dark:hover:bg-primary-950/20 hover:border-primary-600 dark:hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Logga in på Karrelo"
								>
									Logga in
								</Link>
								<Link
									href="/login?type=company"
									className="btn-outline w-full sm:w-auto sm:min-w-[220px] text-center hover:scale-105 transition-all duration-300 hover:bg-primary-50 dark:hover:bg-primary-950/20 hover:border-primary-600 dark:hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Logga in som företag"
								>
									Företagsinloggning
								</Link>
							</div>
						</>
					) : (
						<>
							{/* Company Hero */}
							<div className="space-y-4 sm:space-y-6">
								<h1
									className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold transition-all duration-1000 ${
										visibleSections.has('hero')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{ transitionDelay: '0.2s' }}
								>
									<span className="text-gradient bg-gradient-to-r from-primary-600 via-teal-500 to-primary-600 bg-clip-text text-transparent animate-gradient">
										Karrelo Pro
									</span>
								</h1>
								<div className="space-y-2 sm:space-y-3">
									<p
										className={`text-2xl sm:text-3xl md:text-4xl font-bold text-slate-700 dark:text-slate-300 max-w-4xl mx-auto transition-all duration-1000 ${
											visibleSections.has('hero')
												? 'opacity-100 translate-y-0'
												: 'opacity-0 translate-y-10'
										}`}
										style={{ transitionDelay: '0.4s' }}
									>
										ATS för smart rekrytering
									</p>
									<p
										className={`text-base sm:text-lg md:text-xl font-medium text-slate-600 dark:text-slate-400 max-w-3xl mx-auto transition-all duration-1000 ${
											visibleSections.has('hero')
												? 'opacity-100 translate-y-0'
												: 'opacity-0 translate-y-10'
										}`}
										style={{ transitionDelay: '0.5s' }}
									>
										Byggt på 20+ års rekryteringserfarenhet
									</p>
								</div>
								<p
									className={`text-lg sm:text-xl text-primary-600 dark:text-primary-400 max-w-3xl mx-auto transition-all duration-1000 ${
										visibleSections.has('hero')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{ transitionDelay: '0.6s' }}
								>
									AI-driven jobbannonser, anpassningsbar pipeline och kandidatutvärdering – allt i ett.
								</p>
								<div
									className={`text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-4xl mx-auto transition-all duration-1000 leading-relaxed pt-2 sm:pt-4 space-y-2 ${
										visibleSections.has('hero')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{ transitionDelay: '0.8s' }}
								>
									<p>Karrelo Pro gör rekryteringen smidig för både team och kandidater. Skapa jobbannonser med AI, hantera ansökningar i en pipeline och hitta rätt person snabbare.</p>
								</div>
							</div>

							{/* Company CTA Buttons */}
							<div
								className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center pt-4 sm:pt-6 transition-all duration-1000 w-full sm:w-auto ${
									visibleSections.has('hero')
										? 'opacity-100 translate-y-0'
										: 'opacity-0 translate-y-10'
								}`}
								style={{ transitionDelay: '1.2s' }}
							>
								<Link
									href="/login?type=company"
									className="btn-primary w-full sm:w-auto sm:min-w-[220px] text-center ripple hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Logga in för företag"
								>
									<span className="relative z-10">Företagsinloggning</span>
									<div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
								</Link>
								<Link
									href="/contact"
									className="btn-outline w-full sm:w-auto sm:min-w-[220px] text-center hover:scale-105 transition-all duration-300 hover:bg-primary-50 dark:hover:bg-primary-950/20 hover:border-primary-600 dark:hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Kontakta oss för demo"
								>
									Kontakta oss
								</Link>
							</div>
						</>
					)}
				</div>
			</section>

			{/* Expertise Section (Private) / Value Proposition (Company) */}
			<section
				ref={expertiseRef}
				id="expertise"
				className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-primary-50/30 dark:via-primary-950/20 to-transparent"
				aria-labelledby="expertise-heading"
			>
				<div className="max-w-6xl mx-auto">
					<div
						className={`card-glass rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 transition-all duration-1000 ${
							visibleSections.has('expertise')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-20'
						}`}
					>
						{viewMode === 'private' ? (
							<div className="space-y-10 sm:space-y-12">
								<div className="text-center space-y-4 sm:space-y-5">
									<div className="inline-block px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-sm font-semibold">
										Byggt på verklig expertis
									</div>
									<h2 id="expertise-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100">
										Utvecklat av karriärcoacher och rekryterare
									</h2>
									<p className="text-xl sm:text-2xl font-medium text-slate-600 dark:text-slate-400">
										med 20+ års erfarenhet
									</p>
								</div>
								<div className="text-center space-y-4 max-w-3xl mx-auto">
									<p className="text-lg sm:text-xl text-slate-700 dark:text-slate-300 leading-relaxed">
										Karrelo är inte bara AI-teknologi – det är tjugo års erfarenhet av vad som faktiskt fungerar i jobbsökning och rekrytering.
									</p>
									<p className="text-lg sm:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
										Vårt team av karriärcoacher och rekryterare har byggt systemet utifrån verklig kunskap om vad som får ansökningar att lyckas.
									</p>
								</div>
								<div className="space-y-6 sm:space-y-8">
									<div className="text-center">
										<h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
											Våra experter
										</h3>
										<div className="w-16 h-1 bg-gradient-to-r from-primary-600 to-teal-600 mx-auto rounded-full"></div>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
										<div className="card-base rounded-xl sm:rounded-2xl p-5 sm:p-6 space-y-3 hover-lift border-l-4 border-l-primary-500">
											<div className="flex items-center gap-3 mb-3">
												<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center text-white font-bold flex-shrink-0">
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
													</svg>
												</div>
												<h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-slate-100">Rekryterare</h3>
											</div>
											<p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
												Våra rekryterare vet exakt vad som får en ansökan att sticka ut. De har sett tusentals ansökningar och vet vad som fungerar.
											</p>
										</div>
										<div className="card-base rounded-xl sm:rounded-2xl p-5 sm:p-6 space-y-3 hover-lift border-l-4 border-l-teal-500">
											<div className="flex items-center gap-3 mb-3">
												<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-primary-500 flex items-center justify-center text-white font-bold flex-shrink-0">
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
													</svg>
												</div>
												<h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-slate-100">Karriärcoacher</h3>
											</div>
											<p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
												Våra coacher har hjälpt hundratals personer att nå sina drömjobb. De vet vad som krävs för att lyckas.
											</p>
										</div>
										<div className="card-base rounded-xl sm:rounded-2xl p-5 sm:p-6 space-y-3 hover-lift border-l-4 border-l-primary-400">
											<div className="flex items-center gap-3 mb-3">
												<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-400 to-teal-400 flex items-center justify-center text-white font-bold flex-shrink-0">
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
													</svg>
												</div>
												<h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-slate-100">Verklig kunskap</h3>
											</div>
											<p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
												Allt i Karrelo bygger på beprövade metoder – inte bara teori. Vi vet vad som faktiskt fungerar.
											</p>
										</div>
									</div>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 pt-8 sm:pt-10 border-t-2 border-slate-200 dark:border-slate-700">
									<div className="text-center space-y-3 p-6 rounded-xl bg-gradient-to-br from-primary-50 to-teal-50 dark:from-primary-950/30 dark:to-teal-950/30">
										<div className="text-4xl sm:text-5xl font-bold text-primary-600 dark:text-primary-400">20+</div>
										<div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100">Års erfarenhet</div>
										<div className="text-sm sm:text-base text-slate-600 dark:text-slate-400">I rekrytering och karriärcoaching</div>
									</div>
									<div className="text-center space-y-3 p-6 rounded-xl bg-gradient-to-br from-teal-50 to-primary-50 dark:from-teal-950/30 dark:to-primary-950/30">
										<div className="text-4xl sm:text-5xl font-bold text-primary-600 dark:text-primary-400">Experter</div>
										<div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100">Karriärcoacher & rekryterare</div>
										<div className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Som har byggt systemet</div>
									</div>
								</div>
							</div>
						) : (
							<div className="space-y-10 sm:space-y-12">
								<div className="text-center space-y-4 sm:space-y-5">
									<div className="inline-block px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-sm font-semibold">
										Mer än ett ATS
									</div>
									<h2 id="expertise-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100">
										Optimera rekryteringen
									</h2>
									<p className="text-xl sm:text-2xl font-medium text-slate-600 dark:text-slate-400">
										Karrelo Pro gör rekryteringen smidig för både team och kandidater
									</p>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
									<div className="card-base rounded-xl sm:rounded-2xl p-6 sm:p-8 hover-lift border-l-4 border-l-primary-500">
										<h3 className="font-bold text-xl sm:text-2xl text-slate-900 dark:text-slate-100 mb-3">
											Rekrytera mer effektivt med AI
										</h3>
										<p className="text-slate-600 dark:text-slate-400 leading-relaxed">
											Skapa jobbannonser med AI, extrahera information från er webbplats och få färdiga beskrivningar på sekunder. Hitta rätt kandidater snabbare. Få tid över till det som verkligen räknas.
										</p>
									</div>
									<div className="card-base rounded-xl sm:rounded-2xl p-6 sm:p-8 hover-lift border-l-4 border-l-teal-500">
										<h3 className="font-bold text-xl sm:text-2xl text-slate-900 dark:text-slate-100 mb-3">
											Hitta rätt kandidater snabbare
										</h3>
										<p className="text-slate-600 dark:text-slate-400 leading-relaxed">
											AI-poängsättning av kandidater, anpassningsbar pipeline och tydlig överblick över alla ansökningar. Samarbeta med teamet via anteckningar och e-postmallar.
										</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</section>

			{/* Stats Section */}
			<section
				ref={statsRef}
				id="stats"
				className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-slate-50/50 dark:via-slate-900/50 to-transparent"
				aria-label="Statistik"
			>
				<div className="max-w-6xl mx-auto">
					<div
						className={`grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 transition-all duration-1000 ${
							visibleSections.has('stats')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-20'
						}`}
					>
						{viewMode === 'private' ? (
							<>
								<div className="text-center space-y-2">
									<div className="text-5xl md:text-6xl font-bold text-primary-600 dark:text-primary-400">3x</div>
									<div className="text-xl font-semibold text-slate-800 dark:text-slate-200">Högre svarstakt</div>
									<div className="text-slate-600 dark:text-slate-400">Med färre men bättre ansökningar</div>
								</div>
								<div className="text-center space-y-2">
									<div className="text-5xl md:text-6xl font-bold text-primary-600 dark:text-primary-400">85%</div>
									<div className="text-xl font-semibold text-slate-800 dark:text-slate-200">Bättre matchning</div>
									<div className="text-slate-600 dark:text-slate-400">Tack vare AI-driven analys</div>
								</div>
								<div className="text-center space-y-2">
									<div className="text-5xl md:text-6xl font-bold text-primary-600 dark:text-primary-400">50%</div>
									<div className="text-xl font-semibold text-slate-800 dark:text-slate-200">Mindre tid</div>
									<div className="text-slate-600 dark:text-slate-400">På ansökningsprocessen</div>
								</div>
							</>
						) : (
							<>
								<div className="text-center space-y-2">
									<div className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">AI-drivet</div>
									<div className="text-xl font-semibold text-slate-800 dark:text-slate-200">Jobbannonser</div>
									<div className="text-slate-600 dark:text-slate-400">Skapa annonser med AI-hjälp</div>
								</div>
								<div className="text-center space-y-2">
									<div className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">Anpassningsbar</div>
									<div className="text-xl font-semibold text-slate-800 dark:text-slate-200">Pipeline</div>
									<div className="text-slate-600 dark:text-slate-400">Skräddarsy rekryteringsflödet</div>
								</div>
								<div className="text-center space-y-2">
									<div className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">Lätt att</div>
									<div className="text-xl font-semibold text-slate-800 dark:text-slate-200">Komma igång</div>
									<div className="text-slate-600 dark:text-slate-400">Snabb onboarding</div>
								</div>
							</>
						)}
					</div>
				</div>
			</section>

			{/* How It Works Section */}
			<section
				ref={howItWorksRef}
				id="how-it-works"
				className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6"
				aria-labelledby="how-it-works-heading"
			>
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-12 sm:mb-16">
						<h2
							id="how-it-works-heading"
							className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 transition-all duration-1000 ${
								visibleSections.has('how-it-works')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-10'
							}`}
						>
							Så här fungerar det
						</h2>
						<p className="text-lg sm:text-xl text-slate-600 dark:text-slate-400">
							{viewMode === 'private' ? 'Tre enkla steg till bättre jobbmatchningar' : 'Tre steg till smartare rekrytering'}
						</p>
					</div>
					
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
						{(viewMode === 'private' ? [
							{
								step: 1,
								icon: (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
									</svg>
								),
								title: 'Skapa din profil',
								description: 'Bygg din profil med dina färdigheter, erfarenhet och utbildning. Vår ESCO-baserade matchning förstår exakt vad du kan.',
								highlight: 'AI analyserar din profil automatiskt'
							},
							{
								step: 2,
								icon: (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
								),
								title: 'Låt AI matcha dig',
								description: 'Vår AI hittar jobb som verkligen passar dig. Se transparent matchningspoäng och förstå exakt varför ett jobb matchar.',
								highlight: 'Personliga rekommendationer'
							},
							{
								step: 3,
								icon: (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
								),
								title: 'Ansök smartare',
								description: 'Få AI-genererade ansökningsbrev optimerade för både ATS-system och rekryterare. Spåra alla dina ansökningar på ett ställe.',
								highlight: 'Färre ansökningar, bättre resultat'
							}
						] : [
							{
								step: 1,
								icon: (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
									</svg>
								),
								title: 'Skapa företag & publicera jobb',
								description: 'Registrera ert företag och skapa jobbannonser med AI. Ange webbplats och få färdig beskrivning. Publicera jobb och nå kandidater.',
								highlight: 'AI-genererade jobbannonser'
							},
							{
								step: 2,
								icon: (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
								),
								title: 'Ta emot ansökningar & AI-poäng',
								description: 'Kandidater ansöker via Karrelo. Få AI-poängsättning av varje kandidat. Se CV, personligt brev och screening-svar direkt i översikten.',
								highlight: 'AI-kandidatutvärdering'
							},
							{
								step: 3,
								icon: (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
								),
								title: 'Hantera pipeline & anställ',
								description: 'Dra kandidater genom era faser. Samarbeta med teamet via anteckningar. Skicka e-post med mallar. Anställ rätt person.',
								highlight: 'Anpassningsbar pipeline'
							}
						]).map((item: { step: number; icon: React.ReactNode; title: string; description: string; highlight: string }, index: number) => (
							<div
								key={item.step}
								className={`group relative transition-all duration-700 ${
									visibleSections.has('how-it-works')
										? 'opacity-100 translate-y-0'
										: 'opacity-0 translate-y-20'
								}`}
								style={{ transitionDelay: `${index * 0.1}s` }}
							>
								<div className="relative h-full bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-300 hover:shadow-lg flex flex-col items-center text-center">
									{/* Step number - centered */}
									<div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center text-white font-bold text-2xl shadow-md mb-6">
										{item.step}
									</div>
									
									{/* Icon */}
									<div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 mb-4">
										{item.icon}
									</div>
									
									{/* Title */}
									<h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
										{item.title}
									</h3>
									
									{/* Description */}
									<p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
										{item.description}
									</p>
									
									{/* Highlight */}
									<div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-primary-600 dark:text-primary-400">
										<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
										<span>{item.highlight}</span>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Features Section */}
			<section
				ref={featuresRef}
				id="features"
				className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-slate-50/50 dark:via-slate-900/50 to-transparent"
				aria-labelledby="features-heading"
			>
				<div className="max-w-6xl mx-auto">
					<h2
						id="features-heading"
						className={`text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
							visibleSections.has('features')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-10'
						}`}
					>
						{viewMode === 'private' ? 'Varför välja Karrelo?' : 'Varför Karrelo Pro?'}
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
						{(viewMode === 'private' ? [
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
								title: 'Hitta jobb som verkligen passar dig',
								description: 'AI som förstår vad du söker och matchar dig med jobb där du faktiskt har en chans.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
								title: 'Ansökningsbrev som faktiskt läses',
								description: 'Automatiskt genererade ansökningsbrev optimerade för både ATS-system och rekryterare.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
								title: 'Få konkreta förslag på förbättringar',
								description: 'Se exakt vad som saknas och få tips på hur du kan förbättra din profil för bättre matchningar.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
								title: 'Transparent matchningspoäng',
								description: 'Förstå exakt varför ett jobb matchar dig med tydlig poängsättning och förklaringar.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
								title: 'Håll koll på alla ansökningar',
								description: 'Organisera och spåra alla dina ansökningar på ett enkelt sätt med vår tracker.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
								title: 'Personliga rekommendationer',
								description: 'Få jobbrekommendationer skräddarsydda för just dig baserat på din unika profil.',
							},
						] : [
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
								title: 'AI-jobbskapande',
								description: 'Skapa jobbannonser med AI, extrahera information från er webbplats och få färdiga beskrivningar på sekunder.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
								title: 'Anpassningsbar pipeline',
								description: 'Skräddarsy rekryteringsflödet efter era behov. Dra och släpp kandidater mellan faser.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
								title: 'Kandidatpoäng med AI',
								description: 'Få AI-poängsättning av varje kandidat. Prioritera de bästa matchningarna direkt.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
								title: 'Team-samarbete',
								description: 'Bjud in teammedlemmar, dela roller (admin, rekryterare, hiring manager) och samarbeta via anteckningar.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
								title: 'E-postmallar & kommunikation',
								description: 'Skicka e-post till kandidater med färdiga mallar. Kommunicera effektivt under hela processen.',
							},
							{
								icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
								title: 'Analys & rapporter',
								description: 'Följ prestanda för era jobb, se kandidatflöde och utnyttja data för bättre rekryteringsbeslut.',
							},
						]).map((feature: { icon: React.ReactNode; title: string; description: string }, index: number) => (
							<div
								key={index}
								className={`card-glass rounded-xl sm:rounded-2xl p-4 sm:p-6 space-y-3 sm:space-y-4 hover-lift transition-all duration-1000 ${
									visibleSections.has('features')
										? 'opacity-100 translate-y-0'
										: 'opacity-0 translate-y-20'
								}`}
								style={{ transitionDelay: `${index * 0.1}s` }}
							>
								<div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 mb-3">
									{feature.icon}
								</div>
								<h3 className="text-xl font-semibold">{feature.title}</h3>
								<p className="text-slate-600 dark:text-slate-400">
									{feature.description}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Testimonials Section (Private only) */}
			{viewMode === 'private' && (
			<section
				ref={testimonialsRef}
				id="testimonials"
				className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6"
				aria-labelledby="testimonials-heading"
			>
				<div className="max-w-6xl mx-auto">
					<h2
						id="testimonials-heading"
						className={`text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
							visibleSections.has('testimonials')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-10'
						}`}
					>
						Vad säger våra användare?
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
						{[
							{
								quote:
									'Under testningen fick jag en jobbmatch och fick jobbet. Det känns otroligt att det faktiskt fungerade!',
								author: 'Josefina',
								role: 'Ekonomiassistent',
							},
							{
								quote:
									'Det känns som att jag har min egen jobbcoach, men tillgänglig 24 timmar om dygnet, 7 dagar i veckan. Det här är en game changer. Är fortfarande i intervjufasen.',
								author: 'Martin',
								role: 'Key Account Manager',
							},
							{
								quote:
									'Efter tre veckor av testning fick jag jobbet! Karrelo hjälpte mig att hitta rätt matchningar och skriva bättre ansökningar.',
								author: 'Youssef',
								role: 'Lager',
							},
							{
								quote:
									'Det tog bara 10 dagar efter att jag fick testkontot! Transparenta matchningspoäng hjälpte mig att fokusera på rätt jobb istället för att skicka massor av ansökningar.',
								author: 'Sara',
								role: 'Projektledare',
							},
						].map((testimonial, index) => (
							<article
								key={index}
								className={`card-glass rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 hover-lift transition-all duration-1000 ${
									visibleSections.has('testimonials')
										? 'opacity-100 translate-y-0'
										: 'opacity-0 translate-y-20'
								}`}
								style={{ transitionDelay: `${index * 0.2}s` }}
								aria-label={`Testimonial från ${testimonial.author}`}
							>
								<div className="text-4xl text-primary-500 mb-4" aria-hidden="true">"</div>
								<blockquote className="text-lg text-slate-700 dark:text-slate-300 mb-6 italic">
									{testimonial.quote}
								</blockquote>
								<footer className="flex items-center gap-4">
									<div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-teal-400 flex items-center justify-center text-white font-bold" aria-hidden="true">
										{testimonial.author[0]}
									</div>
									<div>
										<cite className="font-semibold not-italic">{testimonial.author}</cite>
										<div className="text-sm text-slate-600 dark:text-slate-400">
											{testimonial.role}
										</div>
									</div>
								</footer>
							</article>
						))}
					</div>
				</div>
			</section>
			)}

			{/* FAQ Section */}
			<section
				ref={faqRef}
				id="faq"
				className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-slate-50/50 dark:via-slate-900/50 to-transparent"
				aria-labelledby="faq-heading"
			>
				<div className="max-w-4xl mx-auto">
					<h2
						id="faq-heading"
						className={`text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
							visibleSections.has('faq')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-10'
						}`}
					>
						Vanliga frågor
					</h2>
					<div className="space-y-3 sm:space-y-4">
						{(viewMode === 'private' ? faqs : companyFaqs).map((faq, index) => (
							<div
								key={index}
								className={`card-glass rounded-lg sm:rounded-xl overflow-hidden transition-all duration-1000 ${
									visibleSections.has('faq')
										? 'opacity-100 translate-y-0'
										: 'opacity-0 translate-y-20'
								}`}
								style={{ transitionDelay: `${index * 0.1}s` }}
							>
								<button
									onClick={() =>
										setExpandedFaq(expandedFaq === index ? null : index)
									}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setExpandedFaq(expandedFaq === index ? null : index);
										}
									}}
									aria-expanded={expandedFaq === index}
									aria-controls={`faq-answer-${index}`}
									className="w-full p-4 sm:p-6 text-left flex items-center justify-between hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900 rounded-t-lg sm:rounded-t-xl min-h-[44px]"
									id={`faq-question-${index}`}
								>
									<span className="text-base sm:text-lg font-semibold pr-3 sm:pr-4">
										{faq.question}
									</span>
									<span
										className={`text-2xl transition-transform duration-300 ${
											expandedFaq === index ? 'rotate-180' : ''
										}`}
										aria-hidden="true"
									>
										▼
									</span>
								</button>
								<div
									id={`faq-answer-${index}`}
									role="region"
									aria-labelledby={`faq-question-${index}`}
									className={`overflow-hidden transition-all duration-300 ${
										expandedFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
									}`}
								>
									<div className="p-4 sm:p-6 pt-0 text-sm sm:text-base text-slate-600 dark:text-slate-400">
										{faq.answer}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Final CTA Section */}
			<section className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6" aria-labelledby="cta-heading">
				<div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8">
					<h2 id="cta-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold">
						{viewMode === 'private' ? 'Redo att hitta ditt drömjobb?' : 'Upplev Karrelo Pro'}
					</h2>
					<p className="text-lg sm:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
						{viewMode === 'private'
							? 'Börja idag och upplev skillnaden. Färre ansökningar, bättre resultat, mer tid till det som verkligen räknas.'
							: 'Logga in eller kontakta oss för en demo. Vi hjälper er att komma igång med smartare rekrytering.'}
					</p>
					<div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center pt-2 sm:pt-4">
						{viewMode === 'private' ? (
							<>
								<Link
									href="/login"
									className="btn-primary w-full sm:w-auto sm:min-w-[220px] text-center ripple hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Logga in på Karrelo"
								>
									<span className="relative z-10">Logga in</span>
									<div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
								</Link>
								<Link
									href="/signup"
									className="btn-outline w-full sm:w-auto sm:min-w-[220px] text-center hover:scale-105 transition-all duration-300 hover:bg-primary-50 dark:hover:bg-primary-950/20 hover:border-primary-600 dark:hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Skapa konto på Karrelo"
								>
									Skapa konto
								</Link>
							</>
						) : (
							<>
								<Link
									href="/login?type=company"
									className="btn-primary w-full sm:w-auto sm:min-w-[220px] text-center ripple hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Logga in för företag"
								>
									<span className="relative z-10">Företagsinloggning</span>
									<div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
								</Link>
								<Link
									href="/contact"
									className="btn-outline w-full sm:w-auto sm:min-w-[220px] text-center hover:scale-105 transition-all duration-300 hover:bg-primary-50 dark:hover:bg-primary-950/20 hover:border-primary-600 dark:hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
									aria-label="Kontakta oss för demo"
								>
									Kontakta oss
								</Link>
							</>
						)}
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="relative z-10 w-full p-4 sm:p-6 md:p-8 border-t border-slate-200 dark:border-slate-800 mt-8 sm:mt-12 bg-slate-50/50 dark:bg-slate-900/50" role="contentinfo">
				<div className="max-w-6xl mx-auto">
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
						<div className="space-y-4">
							<h3 className="font-semibold text-slate-900 dark:text-slate-100">Karrelo</h3>
							<p className="text-sm text-slate-600 dark:text-slate-400">
								AI-driven jobbsökning med färre men bättre ansökningar.
							</p>
						</div>
						<div className="space-y-4">
							<h3 className="font-semibold text-slate-900 dark:text-slate-100">Produkt</h3>
							<ul className="space-y-2 text-sm">
								<li>
									<Link href="/signup" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
										Skapa konto
									</Link>
								</li>
								<li>
									<Link href="/login" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
										Logga in
									</Link>
								</li>
								<li>
									<Link href="/login?type=company" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
										Företagsinloggning
									</Link>
								</li>
							</ul>
						</div>
						<div className="space-y-4">
							<h3 className="font-semibold text-slate-900 dark:text-slate-100">Juridik</h3>
							<ul className="space-y-2 text-sm">
								<li>
									<Link href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
										Integritetspolicy
									</Link>
								</li>
								<li>
									<Link href="/terms" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
										Användarvillkor
									</Link>
								</li>
								<li>
									<Link href="/contact" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
										Kontakt
									</Link>
								</li>
							</ul>
						</div>
						<div className="space-y-4">
							<h3 className="font-semibold text-slate-900 dark:text-slate-100">Följ oss</h3>
							<div className="flex gap-4">
								{/* Add social media links when available */}
								<a href="#" className="text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" aria-label="LinkedIn">
									<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
										<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
									</svg>
								</a>
							</div>
						</div>
					</div>
					<div className="pt-8 border-t border-slate-200 dark:border-slate-800 text-center text-sm text-slate-500 dark:text-slate-400">
						<p>© {new Date().getFullYear()} Karrelo. Alla rättigheter förbehållna. i samarbete med <a href="https://jobbstore.se" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">JobbStore</a></p>
					</div>
				</div>
			</footer>
		</main>
		</>
	);
}

export default function Home() {
	return <HomeContent />;
}
